import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { X, ArrowRight, Sparkles, Check, Copy, Gift, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import useEffectivePricing, { formatPrice } from "@/lib/useEffectivePricing";
import usePromoStatus from "@/lib/usePromoStatus";

/**
 * Upgrade nudge modal for Free + Basic members.
 *
 * When it fires:
 *   1. The user has finished (or skipped) the guided tour
 *      (`user.tour_completed === true`).
 *   2. The user is on the `free` or `basic` tier — Premium members never
 *      see it.
 *   3. At least `COOLDOWN_DAYS` have passed since the last time we showed
 *      or the user dismissed it (`localStorage.summit_last_upgrade_nudge`).
 *
 * What it shows:
 *   • Free  → both Basic and Premium tier cards
 *   • Basic → Premium card only, priced at the loyalty upgrade rate
 *   • A referral section under the cards showing the user's own code +
 *     share link and the coupon they earn after N successful referrals.
 *
 * Dismiss ("Not now") stamps the cooldown. Clicking a tier card jumps
 * straight to `/checkout/agreement/{tier}`.
 */
const COOLDOWN_DAYS_BY_TIER = {
  free: 3,
  basic: 7,
};
const LAST_NUDGE_KEY = "summit_last_upgrade_nudge";

function daysSince(iso) {
  if (!iso) return Infinity;
  try {
    return (Date.now() - new Date(iso).getTime()) / 86400000;
  } catch {
    return Infinity;
  }
}

export default function WelcomeUpgradeModal({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { params: P } = usePromoStatus();
  const [open, setOpen] = useState(false);
  const [referral, setReferral] = useState(null);
  const pricing = useEffectivePricing();

  useEffect(() => {
    if (!user || user === false) return;
    const tier = user.tier || "free";
    if (tier === "premium") return;
    if (!user.tour_completed) return; // wait for tour end/skip

    const cooldown = COOLDOWN_DAYS_BY_TIER[tier] ?? 7;
    let lastShownIso = null;
    try {
      lastShownIso = localStorage.getItem(LAST_NUDGE_KEY);
    } catch { /* ignore */ }
    if (daysSince(lastShownIso) < cooldown) return;

    // Slight delay so the dashboard finishes painting first (avoids
    // the modal popping in mid-layout on slow devices).
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, [user]);

  // Force-open the modal the instant the user finishes (or skips) the
  // guided tour. This bypasses the "cooldown" gate for that one moment
  // — the tour end IS the moment to convert. `OnboardingTour.jsx`
  // dispatches `pms:tour-completed` once persistCompletion resolves.
  useEffect(() => {
    if (!user || user === false) return;
    const tier = user.tier || "free";
    if (tier === "premium") return;

    const handler = () => setOpen(true);
    window.addEventListener("pms:tour-completed", handler);
    return () => window.removeEventListener("pms:tour-completed", handler);
  }, [user]);

  useEffect(() => {
    if (!open) return;
    // Lazy-fetch referral data only when we're actually about to show.
    api.get("/referrals/me").then((r) => setReferral(r.data)).catch(() => {});
  }, [open]);

  const stampCooldown = () => {
    try { localStorage.setItem(LAST_NUDGE_KEY, new Date().toISOString()); } catch { /* ignore */ }
  };

  const dismiss = () => {
    stampCooldown();
    setOpen(false);
  };

  const upgrade = (tier) => {
    stampCooldown();
    navigate(`/checkout/agreement/${tier}`);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") dismiss(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyCode = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("welcomeModal.codeCopied"));
    } catch {
      toast.error(t("welcomeModal.copyFail"));
    }
  };

  const copyLinkFallback = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("welcomeModal.linkCopied"));
    } catch {
      toast.error(t("welcomeModal.copyFail"));
    }
  };

  // Native share sheet on mobile (WhatsApp, iMessage, Discord, etc.),
  // graceful clipboard fallback on desktop. Users are ~3x more likely to
  // complete a share when they don't have to switch apps and paste.
  const shareLink = async (url) => {
    const shareData = {
      title: t("welcomeModal.chip"),
      text: t("welcomeModal.shareText"),
      url,
    };
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share(shareData);
        toast.success(t("welcomeModal.shareThanks"));
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }
    await copyLinkFallback(url);
  };

  if (!open) return null;

  const tier = user?.tier || "free";
  const archetype = user?.onboarding?.archetype || null;

  // Which tier cards to render.
  const cards = tier === "basic"
    ? [
        {
          tier: "premium", label: t("tier.premium"),
          priceLabel: pricing.premium ? formatPrice(pricing.premium.effective) : "$1,250",
          baseLabel:  pricing.premium?.discounted ? formatPrice(pricing.premium.base) : null,
          promoLine:  pricing.premium?.promoStage === "free"
                        ? t("welcomeModal.seatsFree", { count: 10 })
                        : t("welcomeModal.loyaltyUpgrade"),
          bullets: [
            t("welcomeModal.bulletAscent"),
            t("welcomeModal.bulletResidency"),
            t("welcomeModal.bulletMastery"),
            t("welcomeModal.bulletRealtimeSetups"),
          ],
          tone: "amber", popular: true,
        },
      ]
    : [
        {
          tier: "basic", label: t("tier.basic"),
          priceLabel: pricing.basic ? formatPrice(pricing.basic.effective) : "$750",
          baseLabel:  pricing.basic?.discounted ? formatPrice(pricing.basic.base) : null,
          promoLine:  pricing.basic?.promoStage === "free"
                        ? t("welcomeModal.seatsFree", { count: P.freeCap })
                        : pricing.basic?.promoStage === "half"
                          ? t("welcomeModal.seatsHalf", P)
                          : null,
          bullets: [
            t("welcomeModal.bulletFullCurriculum"),
            t("welcomeModal.bulletBasicCerts"),
            t("welcomeModal.bulletWeeklyReports"),
          ],
          tone: "blue",
        },
        {
          tier: "premium", label: t("tier.premium"),
          priceLabel: pricing.premium ? formatPrice(pricing.premium.effective) : "$1,250",
          baseLabel:  pricing.premium?.discounted ? formatPrice(pricing.premium.base) : null,
          promoLine:  pricing.premium?.promoStage === "free"
                        ? t("welcomeModal.seatsFree", { count: 10 })
                        : pricing.premium?.discounted
                          ? t("welcomeModal.onSale")
                          : null,
          bullets: [
            t("welcomeModal.bulletAscent"),
            t("welcomeModal.bulletResidency"),
            t("welcomeModal.bulletMastery"),
          ],
          tone: "amber", popular: true,
        },
      ];

  const headline = tier === "basic"
    ? t("welcomeModal.headlineBasic")
    : t("welcomeModal.headlineFree");

  const subhead = tier === "basic"
    ? t("welcomeModal.subheadBasic")
    : archetype
      ? (
          <Trans
            i18nKey="welcomeModal.subheadFreeArch"
            values={{ archetype }}
            components={[<span className="font-semibold text-amber-200" />]}
          />
        )
      : t("welcomeModal.subheadFree");

  return (
    <div
      data-testid="welcome-upgrade-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white rounded-3xl shadow-2xl border border-slate-200">
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("welcomeModal.dismiss")}
          data-testid="welcome-upgrade-dismiss"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"
        >
          <X size={16} />
        </button>

        {/* Header — dark strip */}
        <div className="relative bg-slate-950 text-white px-8 py-8 overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-blue-600/25 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.28em] font-bold text-amber-300">
              <Sparkles size={11} /> {t("welcomeModal.chip")}
            </div>
            <h2 className="mt-4 font-display font-black text-3xl sm:text-4xl leading-tight tracking-tighter">
              {headline}
            </h2>
            <p className="mt-2 text-sm text-slate-300 max-w-lg leading-relaxed">
              {subhead}
            </p>
          </div>
        </div>

        {/* Tier cards */}
        <div className={`p-6 sm:p-8 grid gap-3 ${cards.length === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
          {cards.map((c) => (
            <button
              key={c.tier}
              type="button"
              onClick={() => upgrade(c.tier)}
              data-testid={`welcome-upgrade-${c.tier}`}
              className={`relative text-left rounded-2xl border-2 p-5 transition-all hover:-translate-y-0.5 ${
                c.tone === "amber"
                  ? "border-amber-400 bg-amber-50/50 hover:shadow-[0_16px_36px_-16px_rgba(245,158,11,0.4)]"
                  : "border-slate-200 hover:border-blue-300 hover:shadow-[0_16px_36px_-16px_rgba(30,64,175,0.2)]"
              }`}
            >
              {c.popular && (
                <div className="absolute -top-2.5 right-4 text-[9px] uppercase tracking-widest font-bold bg-amber-500 text-slate-950 px-2 py-0.5 rounded-full">
                  {t("welcomeModal.popular")}
                </div>
              )}
              <div className="flex items-baseline justify-between gap-3">
                <div className="font-display font-black text-2xl text-slate-900 tracking-tight">{c.label}</div>
                <div className="flex items-baseline gap-2">
                  {c.baseLabel && (
                    <div className="font-mono text-xs text-slate-400 line-through">{c.baseLabel}</div>
                  )}
                  <div className="font-mono font-bold text-lg text-slate-900">{c.priceLabel}</div>
                </div>
              </div>
              {c.promoLine && (
                <div className={`mt-0.5 text-[10px] uppercase tracking-widest font-bold ${
                  c.tone === "amber" ? "text-amber-700" : "text-blue-800"
                }`}>{c.promoLine}</div>
              )}
              <ul className="mt-4 space-y-1.5">
                {c.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-[13px] text-slate-700">
                    <Check size={13} className={`shrink-0 mt-0.5 ${c.tone === "amber" ? "text-amber-600" : "text-blue-800"}`} />
                    {b}
                  </li>
                ))}
              </ul>
              <div className={`mt-4 inline-flex items-center gap-1 text-xs font-semibold ${
                c.tone === "amber" ? "text-amber-700" : "text-blue-800"
              }`}>
                {t("welcomeModal.continueCheckout")} <ArrowRight size={12} />
              </div>
            </button>
          ))}
        </div>

        {/* Referral section — encourages the discount path */}
        {referral && (
          <div data-testid="welcome-upgrade-referral" className="mx-6 sm:mx-8 mb-6 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <Gift size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-slate-900 text-sm">
                  {referral.coupon_unlocked && !referral.coupon_redeemed
                    ? (
                        <Trans
                          i18nKey="welcomeModal.referralUnlocked"
                          values={{ percent: referral.discount_percent }}
                          components={[<span className="text-emerald-700" />]}
                        />
                      )
                    : referral.remaining > 0
                      ? (
                          <Trans
                            i18nKey="welcomeModal.referralRemaining"
                            values={{
                              count: referral.remaining,
                              percent: referral.discount_percent,
                              savings: formatPrice(referral.premium_savings),
                            }}
                            components={[<span className="text-emerald-700" />]}
                          />
                        )
                      : t("welcomeModal.referralReady")}
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  {t("welcomeModal.referralProgress", { done: referral.referred_activated, required: referral.required })}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => copyCode(referral.display_code)}
                    data-testid="welcome-upgrade-copy-code"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-emerald-200 px-3 py-1.5 text-xs font-mono font-bold text-emerald-900 hover:bg-emerald-100 transition-colors"
                  >
                    {referral.display_code}
                    <Copy size={11} />
                  </button>
                  {referral.share_url && (
                    <button
                      type="button"
                      onClick={() => shareLink(referral.share_url)}
                      data-testid="welcome-upgrade-copy-link"
                      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-emerald-700 transition-colors"
                    >
                      {typeof navigator !== "undefined" && typeof navigator.share === "function"
                        ? <>{t("welcomeModal.shareLink")} <Share2 size={11} /></>
                        : <>{t("welcomeModal.copyLink")} <Copy size={11} /></>}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="px-8 pb-6 flex items-center justify-between gap-3 border-t border-slate-100">
          <div className="text-xs text-slate-500 pt-4">
            {t("welcomeModal.footerSecure")}
          </div>
          <Button
            type="button"
            onClick={dismiss}
            variant="ghost"
            data-testid="welcome-upgrade-explore"
            className="mt-3 text-slate-500 hover:text-slate-900"
          >
            {t("welcomeModal.notNow")}
          </Button>
        </div>
      </div>
    </div>
  );
}
